import React, { useState, useEffect } from "react";
import {
  Globe,
  Key,
  Link2,
  RefreshCw,
  Save,
  TestTube,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings2,
  Eye,
  EyeOff
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { apiRequest, formatDateTime } from "../lib/utils";
import { toast } from "sonner";

const integrationIcons = {
  meta_ads: Globe,
  servall_crm: Link2,
  field_marketing: Settings2,
  other_website: Globe
};

const IntegrationCard = ({ integration, onUpdate, onTest }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [localData, setLocalData] = useState({
    is_active: integration.is_active,
    endpoint_url: integration.endpoint_url || "",
    credentials: {
      api_key: "",
      api_secret: "",
      access_token: ""
    }
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const Icon = integrationIcons[integration.integration_name] || Globe;

  const getStatusColor = (status) => {
    switch (status) {
      case "connected":
        return "text-green-600 bg-green-50";
      case "error":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="w-4 h-4" />;
      case "error":
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        is_active: localData.is_active,
        endpoint_url: localData.endpoint_url || null,
        credentials: {}
      };

      // Only include credentials that have values and don't start with ****
      Object.entries(localData.credentials).forEach(([key, value]) => {
        if (value && !value.startsWith("****")) {
          updateData.credentials[key] = value;
        }
      });

      await onUpdate(integration.integration_name, updateData);
      setIsEditing(false);
      toast.success("Integration settings saved");
    } catch (error) {
      toast.error(error.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await onTest(integration.integration_name);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error.message || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getMaskedValue = (field) => {
    if (integration.credentials_masked && integration.credentials_masked[field]) {
      return integration.credentials_masked[field];
    }
    return "";
  };

  return (
    <Card className="bento-card" data-testid={`integration-${integration.integration_name}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${integration.is_active ? "bg-blue-50" : "bg-gray-50"}`}>
              <Icon className={`w-5 h-5 ${integration.is_active ? "text-blue-600" : "text-gray-400"}`} />
            </div>
            <div>
              <CardTitle className="text-base font-heading">{integration.display_name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(integration.connection_status)}`}>
                  {getStatusIcon(integration.connection_status)}
                  {integration.connection_status === "connected" ? "Connected" : 
                   integration.connection_status === "error" ? "Error" : "Disconnected"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor={`active-${integration.integration_name}`} className="text-xs text-muted-foreground">
              {localData.is_active ? "Active" : "Inactive"}
            </Label>
            <Switch
              id={`active-${integration.integration_name}`}
              checked={localData.is_active}
              onCheckedChange={(checked) => setLocalData(prev => ({ ...prev, is_active: checked }))}
              data-testid={`toggle-${integration.integration_name}`}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Last Sync Time */}
        {integration.last_sync && (
          <div className="text-xs text-muted-foreground">
            Last sync: {formatDateTime(integration.last_sync)}
          </div>
        )}

        {/* Credential Fields */}
        <div className="space-y-3">
          <div>
            <Label htmlFor={`endpoint-${integration.integration_name}`} className="text-sm">
              Endpoint URL
            </Label>
            <Input
              id={`endpoint-${integration.integration_name}`}
              value={localData.endpoint_url}
              onChange={(e) => setLocalData(prev => ({ ...prev, endpoint_url: e.target.value }))}
              placeholder="https://api.example.com/v1"
              className="mt-1"
              data-testid={`endpoint-${integration.integration_name}`}
            />
          </div>

          <div>
            <Label htmlFor={`apikey-${integration.integration_name}`} className="text-sm">
              API Key
            </Label>
            <div className="relative mt-1">
              <Input
                id={`apikey-${integration.integration_name}`}
                type={showSecrets.api_key ? "text" : "password"}
                value={localData.credentials.api_key}
                onChange={(e) => setLocalData(prev => ({
                  ...prev,
                  credentials: { ...prev.credentials, api_key: e.target.value }
                }))}
                placeholder={getMaskedValue("api_key") || "Enter API Key"}
                data-testid={`apikey-${integration.integration_name}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => toggleShowSecret("api_key")}
              >
                {showSecrets.api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor={`secret-${integration.integration_name}`} className="text-sm">
              API Secret
            </Label>
            <div className="relative mt-1">
              <Input
                id={`secret-${integration.integration_name}`}
                type={showSecrets.api_secret ? "text" : "password"}
                value={localData.credentials.api_secret}
                onChange={(e) => setLocalData(prev => ({
                  ...prev,
                  credentials: { ...prev.credentials, api_secret: e.target.value }
                }))}
                placeholder={getMaskedValue("api_secret") || "Enter API Secret"}
                data-testid={`secret-${integration.integration_name}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => toggleShowSecret("api_secret")}
              >
                {showSecrets.api_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor={`token-${integration.integration_name}`} className="text-sm">
              Access Token
            </Label>
            <div className="relative mt-1">
              <Input
                id={`token-${integration.integration_name}`}
                type={showSecrets.access_token ? "text" : "password"}
                value={localData.credentials.access_token}
                onChange={(e) => setLocalData(prev => ({
                  ...prev,
                  credentials: { ...prev.credentials, access_token: e.target.value }
                }))}
                placeholder={getMaskedValue("access_token") || "Enter Access Token"}
                data-testid={`token-${integration.integration_name}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => toggleShowSecret("access_token")}
              >
                {showSecrets.access_token ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleTest}
            disabled={testing}
            data-testid={`test-${integration.integration_name}`}
          >
            {testing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TestTube className="w-4 h-4 mr-2" />
            )}
            Test Connection
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid={`save-${integration.integration_name}`}
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const ApiIntegrationsPage = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const data = await apiRequest("/integrations");
      setIntegrations(data);
    } catch (error) {
      toast.error("Failed to fetch integrations");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (integrationName, updateData) => {
    const result = await apiRequest(`/integrations/${integrationName}`, {
      method: "PUT",
      body: JSON.stringify(updateData)
    });
    // Refresh integrations to get updated data
    fetchIntegrations();
    return result;
  };

  const handleTest = async (integrationName) => {
    const result = await apiRequest(`/integrations/${integrationName}/test`, {
      method: "POST"
    });
    // Refresh to get updated status
    fetchIntegrations();
    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="api-integrations-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">API Integrations</h1>
        <p className="text-muted-foreground">
          Connect and manage external data sources for your CRM
        </p>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Secure Credential Storage</p>
              <p className="text-sm text-blue-700 mt-1">
                All API credentials are stored securely and encrypted. Credentials are masked in the UI for security.
                Test the connection after entering credentials to verify the setup.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integration Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.integration_name}
            integration={integration}
            onUpdate={handleUpdate}
            onTest={handleTest}
          />
        ))}
      </div>
    </div>
  );
};

export default ApiIntegrationsPage;
